import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BookOpen, ArrowLeft, Trash2, CheckCircle } from 'lucide-react'
import { sage, neutral, fonts, warm, leather, paper } from '../styles/theme.js'

const EMAIL = 'privacy@mycookbook.io'
const API_URL = import.meta.env.VITE_API_URL || ''

export default function DataDeletion() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true); setError('')
    try {
      const res = await fetch(`${API_URL}/api/users/deletion-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      if (!res.ok) throw new Error('Request failed')
      setSubmitted(true)
    } catch {
      setError('Something went wrong. Please email us directly at ' + EMAIL)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: paper.bg, fontFamily: fonts.serif }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(160deg, ${sage.dark}, ${sage.mid})`,
        borderBottom: `2px solid white`,
        padding: '20px 32px',
        display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <BookOpen size={28} color={leather.gold} />
        <span style={{ fontFamily: fonts.cursive, fontSize: 26, color: leather.gold, flex: 1 }}>
          My Cookbook
        </span>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: `1px solid rgba(255,255,255,0.5)`,
            borderRadius: 6, padding: '6px 12px', cursor: 'pointer',
            color: leather.goldLight, fontFamily: fonts.serif, fontSize: 14,
          }}
        >
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 620, margin: '0 auto', padding: '56px 32px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
          <Trash2 size={32} color={warm.mid} />
          <h1 style={{ fontFamily: fonts.cursive, fontSize: 38, color: neutral[900] }}>
            Data Deletion Request
          </h1>
        </div>
        <p style={{ fontSize: 15, color: neutral[500], fontStyle: 'italic', marginBottom: 40 }}>
          We'll permanently delete your account and all associated data within 30 days.
        </p>

        <div style={{ height: 1, background: `linear-gradient(90deg, ${neutral[300]}, transparent)`, marginBottom: 40 }} />

        {submitted ? (
          <div style={{
            background: '#f0faf4', border: '1px solid #a8d5b5',
            borderRadius: 10, padding: 28, textAlign: 'center',
          }}>
            <CheckCircle size={44} color="#27ae60" style={{ marginBottom: 14 }} />
            <h2 style={{ fontFamily: fonts.serif, fontSize: 22, color: neutral[900], marginBottom: 8 }}>
              Request received
            </h2>
            <p style={{ fontSize: 16, color: neutral[700], lineHeight: 1.6 }}>
              We've received your deletion request for <strong>{email}</strong>.
              Your account and all associated data (recipes, notes, and billing information)
              will be permanently deleted within 30 days. You'll receive a confirmation email
              when it's done.
            </p>
          </div>
        ) : (
          <>
            <div style={{
              background: neutral[0], border: `1px solid ${neutral[300]}`,
              borderRadius: 10, padding: 28, marginBottom: 32,
            }}>
              <h2 style={{ fontFamily: fonts.serif, fontSize: 20, color: neutral[900], marginBottom: 16 }}>
                What gets deleted
              </h2>
              {[
                'Your account and profile information',
                'All recipes, ingredients, and steps',
                'Personal notes and favorites',
                'AI conversation history',
                'Subscription and billing records',
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: warm.mid, flexShrink: 0 }} />
                  <span style={{ fontSize: 16, color: neutral[700] }}>{item}</span>
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label style={{ fontSize: 17, color: neutral[900], fontWeight: 600 }}>
                Your account email address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{
                  padding: '12px 16px', fontSize: 16, fontFamily: fonts.serif,
                  border: `1px solid ${neutral[300]}`, borderRadius: 8,
                  background: neutral[0], color: neutral[900], outline: 'none',
                }}
              />
              {error && (
                <p style={{ fontSize: 14, color: warm.mid }}>{error}</p>
              )}
              <button
                type="submit"
                disabled={loading || !email.trim()}
                style={{
                  padding: '13px', borderRadius: 8,
                  background: loading || !email.trim() ? neutral[300] : warm.mid,
                  border: 'none', color: 'white', cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: fonts.serif, fontSize: 17, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <Trash2 size={16} />
                {loading ? 'Submitting…' : 'Request Account Deletion'}
              </button>
            </form>

            <p style={{ marginTop: 24, fontSize: 14, color: neutral[500], textAlign: 'center' }}>
              Prefer to email us directly?{' '}
              <a href={`mailto:${EMAIL}`} style={{ color: neutral[900] }}>{EMAIL}</a>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
